import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  ArrowLeft,
  Upload,
  Download,
  Lock,
  Plus,
  PenLine,
  CalendarDays,
  Type,
  SquareCheck,
} from "lucide-react";
import "./document-workflows.css";
import DocumentCanvasBuilder from "./DocumentCanvasBuilder";
export const WORKFLOW_TYPES = [
  ["official_form", "Official / existing form"],
  ["internal_form", "Internal form"],
  ["sop_policy", "SOP / policy"],
  ["handbook", "Handbook / manual"],
  ["agreement", "Agreement / acknowledgment"],
  ["reference", "Reference"],
];
const COMPLETIONS = [
  ["read_only", "Read only"],
  ["acknowledge", "Confirm they have read it"],
  ["fill", "Fill in the form"],
  ["sign", "Sign the document"],
  ["fill_sign", "Fill in and sign"],
];
const ROLES = [
  ["massage_therapist", "Massage Therapist"],
  ["front_desk", "Experience Guide"],
  ["boutique_manager", "General Manager"],
];
const block = (kind = "paragraph", text = "") => ({
  kind,
  text,
  level: 1,
  ordered: false,
  rows: [],
});
const initial = (treatment) => ({
  document_type: treatment === "preserve" ? "official_form" : "sop_policy",
  treatment,
  completion: "read_only",
  source_id: null,
  source_mode: "existing",
  blocks: treatment === "preserve" ? [] : [block()],
  fields: [],
  wording_edited: false,
  counter_sign: false,
  onboarding_roles: [],
  sop_template: false,
});
const err = (e) =>
  e.response?.data?.detail ||
  e.message ||
  "The request could not be completed.";
export async function workflowDownload(url, name) {
  const r = await api.get(url, { responseType: "blob" });
  const blob = new Blob([r.data], {
    type: r.headers?.["content-type"] || "application/pdf",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}
function signPair(signer, page = 0) {
  const prefix = "field_" + Math.random().toString(16).slice(2);
  const x = signer === "employee" ? 8 : 54;
  return [
    {
      id: prefix,
      kind: "signature",
      label:
        signer === "employee"
          ? "Employee signature"
          : "General Manager signature",
      page,
      x,
      y: 80,
      width: 38,
      height: 5,
      required: true,
      signer,
      existing_name: null,
      options: [],
    },
    {
      id: prefix + "_date",
      kind: "signing_date",
      label: "System signing date & time",
      page,
      x,
      y: 86,
      width: 38,
      height: 4,
      required: false,
      signer,
      existing_name: null,
      options: [],
    },
  ];
}
function addSignPair(wf, signer, page = 0) {
  return wf.fields.some((f) => f.kind === "signature" && f.signer === signer)
    ? wf.fields
    : [...wf.fields, ...signPair(signer, page)];
}
export function WorkflowPage({
  preview,
  page,
  setPage,
  fields,
  editing,
  onChange,
  selected,
  onSelect,
  values = {},
  onValue,
  completion,
  counter,
  canFill = true,
  activeSigner = "employee",
  canCounterFill = false,
  simulation = false,
  placing = false,
  onPlace,
  emptyMessage = "Upload a document to see its pages here.",
  bottomInset = 105,
  fitContainer = false,
}) {
  const host = useRef(null),
    viewport = useRef(null),
    drag = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0),
    [availableHeight, setAvailableHeight] = useState(400),
    [zoom, setZoom] = useState(false);
  const [pageNumber, setPageNumber] = useState(String(page + 1));
  useEffect(() => setPageNumber(String(page + 1)), [page]);
  const jumpPage = (event) => {
    event.preventDefault();
    const target = Number(pageNumber);
    if (Number.isInteger(target) && target >= 1 && target <= preview.pages.length)
      setPage(target - 1);
  };
  const geometry = preview?.pages?.[page];
  useEffect(() => {
    const fit = () => {
      if (!host.current) return;
      const height =
        fitContainer && viewport.current
          ? viewport.current.clientHeight - 32
          : window.innerHeight -
            host.current.getBoundingClientRect().top -
            bottomInset;
      setAvailableHeight(Math.max(80, height));
      if (viewport.current) {
        const css = window.getComputedStyle(viewport.current);
        setAvailableWidth(Math.max(0, viewport.current.clientWidth - (parseFloat(css.paddingLeft) || 0) - (parseFloat(css.paddingRight) || 0)));
      }
    };
    fit();
    const observer =
      fitContainer && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(fit)
        : null;
    if (viewport.current) observer?.observe(viewport.current);
    window.addEventListener("resize", fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [preview, bottomInset, fitContainer]);
  const move = (event, resize = false) => {
    if (!editing || !selected || selected.existing_name) return;
    const rect = host.current.getBoundingClientRect();
    drag.current = {
      id: selected.id,
      x: event.clientX,
      y: event.clientY,
      rect,
      start: { ...selected },
      resize,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.stopPropagation();
  };
  const dragging = (event) => {
    const d = drag.current;
    if (!d) return;
    const dx = ((event.clientX - d.x) / d.rect.width) * 100,
      dy = ((event.clientY - d.y) / d.rect.height) * 100;
    const update = d.resize
      ? {
          width: Math.max(2, Math.min(100 - d.start.x, d.start.width + dx)),
          height: Math.max(2, Math.min(100 - d.start.y, d.start.height + dy)),
        }
      : {
          x: Math.max(0, Math.min(100 - d.start.width, d.start.x + dx)),
          y: Math.max(0, Math.min(100 - d.start.height, d.start.y + dy)),
        };
    onChange(d.id, update);
  };
  const keyboard = (event, f) => {
    if (!editing || f.existing_name || !event.key.startsWith("Arrow")) return;
    event.preventDefault();
    const step = event.shiftKey ? 2 : 0.5,
      dx =
        event.key === "ArrowRight"
          ? step
          : event.key === "ArrowLeft"
            ? -step
            : 0,
      dy =
        event.key === "ArrowDown" ? step : event.key === "ArrowUp" ? -step : 0;
    onChange(
      f.id,
      event.altKey
        ? {
            width: Math.min(100 - f.x, Math.max(2, f.width + dx)),
            height: Math.min(100 - f.y, Math.max(2, f.height + dy)),
          }
        : {
            x: Math.max(0, Math.min(100 - f.width, f.x + dx)),
            y: Math.max(0, Math.min(100 - f.height, f.y + dy)),
          },
    );
  };
  return (
    <section className="wf-recess" aria-label="Document pages">
      {preview?.image && geometry ? (
        <div
          ref={viewport}
          className={`wf-paper-viewport${zoom ? " is-zoomed" : ""}`}
          style={{
            maxHeight: zoom && !fitContainer ? availableHeight : undefined,
            overflow: zoom ? "auto" : undefined,
          }}
        >
          <div
            ref={host}
            className={"wf-page" + (placing ? " is-placing" : "")}
            onClick={(e) => {
              if (!placing) return;
              const r = host.current.getBoundingClientRect();
              onPlace?.(
                ((e.clientX - r.left) / r.width) * 100,
                ((e.clientY - r.top) / r.height) * 100,
              );
            }}
            style={{
              aspectRatio: `${geometry.width}/${geometry.height}`,
              // WebKit can collapse a size-contained page with percentage-sized
              // artwork in full-width mode. Give it a concrete document height.
              height: zoom && availableWidth ? `${availableWidth * geometry.height / geometry.width}px` : undefined,
              width: zoom
                ? "100%"
                : `min(100%, ${(availableHeight * geometry.width) / geometry.height}px)`,
            }}
          >
            <img
              src={"data:image/png;base64," + preview.image}
              alt={`Document page ${page + 1}`}
            />
            {fields
              .filter((f) => f.page === page)
              .map((f) => {
                const signature =
                  f.signer === "general_manager"
                    ? counter?.signature
                    : completion?.signature;
                const autoFilled = f.prefill && f.prefill !== "none";
                const displayValue =
                  values[f.id] ??
                  (f.prefill === "company" || f.prefill === "text"
                    ? f.prefill_text
                    : "");
                const immutable =
                  (!simulation &&
                    (canCounterFill ? !!counter : !!completion)) ||
                  !canFill ||
                  f.read_only ||
                  autoFilled ||
                  f.signer !== activeSigner;
                return (
                  <div
                    key={f.id}
                    style={{
                      left: f.x + "%",
                      top: f.y + "%",
                      width: f.width + "%",
                      height: f.height + "%",
                    }}
                    className={
                      "wf-overlay " +
                      (editing ? "is-editing " : "") +
                      (selected?.id === f.id ? "is-selected " : "") +
                      (f.kind === "signature" ? "is-signature" : "")
                    }
                    onPointerDown={(event) => {
                      if (editing) {
                        onSelect(f.id);
                        if (!f.existing_name) {
                          const rect = host.current.getBoundingClientRect();
                          drag.current = {
                            id: f.id,
                            x: event.clientX,
                            y: event.clientY,
                            rect,
                            start: { ...f },
                            resize: false,
                          };
                          event.currentTarget.setPointerCapture(
                            event.pointerId,
                          );
                        }
                      }
                    }}
                    onPointerMove={dragging}
                    onPointerUp={() => {
                      drag.current = null;
                    }}
                    onKeyDown={(event) => {
                      if (editing && ["Enter", " "].includes(event.key)) {
                        event.preventDefault();
                        onSelect(f.id);
                      } else keyboard(event, f);
                    }}
                    onClick={() => editing && onSelect(f.id)}
                    role={editing ? "button" : undefined}
                    tabIndex={editing ? 0 : undefined}
                    aria-label={
                      editing
                        ? `${f.existing_name ? "Select" : "Position"} ${f.label}`
                        : undefined
                    }
                    title={f.label}
                  >
                    {editing ? (
                      <>
                        <span>
                          {f.kind === "signing_date" ? (
                            <>
                              <Lock size={10} />
                              Date signed
                            </>
                          ) : f.prefill === "company" ||
                            f.prefill === "text" ? (
                            f.prefill_text
                          ) : f.prefill === "completion_date" ? (
                            "Date completed"
                          ) : (
                            f.label
                          )}
                        </span>
                        {!f.existing_name && selected?.id === f.id && (
                          <button
                            className="wf-resize"
                            aria-label={`Resize ${f.label}`}
                            onPointerDown={(e) => move(e, true)}
                            onPointerMove={dragging}
                            onPointerUp={() => {
                              drag.current = null;
                            }}
                          />
                        )}
                      </>
                    ) : simulation && f.kind === "signature" ? (
                      <input
                        aria-label={f.label}
                        value={values[f.id] || ""}
                        placeholder="Type a sample name"
                        disabled={f.signer !== activeSigner}
                        onChange={(e) => onValue(f.id, e.target.value)}
                      />
                    ) : f.kind === "signature" ? (
                      <span>
                        {signature?.typed_name || "Signature added when signed"}
                      </span>
                    ) : f.kind === "signing_date" ? (
                      <span>
                        {signature?.at
                          ? new Intl.DateTimeFormat("en-US", {
                              timeZone: ["CC", "RNO"].includes(
                                signature.boutique_code,
                              )
                                ? "America/Denver"
                                : "America/New_York",
                            }).format(new Date(signature.at))
                          : "Recorded when signed"}
                      </span>
                    ) : f.kind === "checkbox" ? (
                      <input
                        type="checkbox"
                        aria-label={f.label}
                        checked={!!values[f.id]}
                        disabled={immutable}
                        onChange={(e) => onValue(f.id, e.target.checked)}
                      />
                    ) : f.kind === "choice" ? (
                      <select
                        aria-label={f.label}
                        value={values[f.id] || ""}
                        disabled={immutable}
                        onChange={(e) => onValue(f.id, e.target.value)}
                      >
                        <option value="">Choose</option>
                        {f.options.map((x) => (
                          <option key={x}>{x}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        aria-label={f.label}
                        type={f.kind === "date" ? "date" : "text"}
                        value={displayValue}
                        placeholder={
                          f.prefill === "completion_date"
                            ? "Date added on completion"
                            : undefined
                        }
                        disabled={immutable}
                        onChange={(e) => onValue(f.id, e.target.value)}
                        maxLength={2000}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      ) : (
        <p className="wf-empty">{emptyMessage}</p>
      )}
      <nav className="wf-pagination" aria-label="Document page navigation">
        <button
          type="button"
          disabled={!preview || page === 0}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </button>
        {preview?.pages.length > 10 ? (
          <form className="wf-page-jump" onSubmit={jumpPage}>
            <input aria-label="Page number" type="number" min="1" max={preview.pages.length}
              required value={pageNumber} onChange={event => setPageNumber(event.target.value)}/>
            <span>/ {preview.pages.length}</span>
            <button type="submit">Go</button>
          </form>
        ) : <span>{preview ? `${page + 1} / ${preview.pages.length}` : "No page loaded"}</span>}
        {preview && (
          <button
            type="button"
            aria-pressed={zoom}
            onClick={() => setZoom(!zoom)}
          >
            {zoom ? "Fit page" : "Read at full width"}
          </button>
        )}
        <button
          type="button"
          disabled={!preview || page + 1 >= preview.pages.length}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </nav>
    </section>
  );
}
export function LegacyDocumentWorkflowBuilder({
  initialTreatment = "preserve",
  reference,
  onCreated,
  onCancel,
  existing,
  onSaved,
}) {
  const { user } = useAuth();
  const [wf, setWf] = useState(existing?.workflow || initial(initialTreatment)),
    [title, setTitle] = useState(existing?.title || ""),
    [category, setCategory] = useState(existing?.category || ""),
    [doc, setDoc] = useState(existing || null),
    [preview, setPreview] = useState(null),
    [page, setPage] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [selectedId, setSelectedId] = useState(""),
    [panel, setPanel] = useState("setup"),
    [blank, setBlank] = useState(false),
    [review, setReview] = useState(false),
    [dirty, setDirty] = useState(!existing),
    [sourceName, setSourceName] = useState(""),
    [blockIndex, setBlockIndex] = useState(0),
    [previewKey, setPreviewKey] = useState(""),
    [previewError, setPreviewError] = useState(""),
    [previewAttempt, setPreviewAttempt] = useState(0),
    [workspaceHeight, setWorkspaceHeight] = useState(400);
  const workspace = useRef(null);
  useEffect(() => {
    const el = workspace.current;
    if (!el) return;
    const fit = () =>
      setWorkspaceHeight(Math.max(160, el.getBoundingClientRect().height));
    fit();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    observer?.observe(el);
    window.addEventListener("resize", fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [!!wf, panel]); // eslint-disable-line react-hooks/exhaustive-deps
  const stepHeading = useRef(null);
  const steps = [
    ["setup", "Document"],
    ["prepare", "Fields & signatures"],
    ["review", "Publish"],
  ];
  const stepIndex = steps.findIndex(([id]) => id === panel);
  const ready =
    !!wf && !!title.trim() && (wf.source_mode === "new" || !!wf.source_id);
  const currentPreviewKey = ready
    ? JSON.stringify([wf, title.trim(), category, page, doc?.id])
    : "";
  const previewReady =
    !!preview && previewKey === currentPreviewKey && !previewError;
  const previousPreview = previewKey ? JSON.parse(previewKey) : null;
  // Keep a stable page while positioning overlays, but never show another source or page.
  const samePage =
    previousPreview &&
    previousPreview[0].source_id === wf?.source_id &&
    previousPreview[0].treatment === wf?.treatment &&
    previousPreview[3] === page;
  useEffect(() => {
    stepHeading.current?.focus();
  }, [panel]);
  const input = useRef(null);
  const selected = wf?.fields.find((f) => f.id === selectedId);
  const change = (patch) => {
    setWf((current) => ({ ...current, ...patch }));
    setDirty(true);
    setReview(false);
  };
  const run = async (task) => {
    setBusy(true);
    setError("");
    try {
      return await task();
    } catch (e) {
      setError(
        typeof err(e) === "string"
          ? err(e)
          : "Check the template settings and required fields.",
      );
      return null;
    } finally {
      setBusy(false);
    }
  };
  const payload = () => ({
    title: title.trim(),
    category,
    workflow: wf,
    expected_version: doc?.version,
  });
  useEffect(() => {
    if (!currentPreviewKey) return;
    let cancelled = false;
    setPreviewError("");
    const timer = setTimeout(async () => {
      const [
        workflow,
        documentTitle,
        libraryCategory,
        requestedPage,
        documentId,
      ] = JSON.parse(currentPreviewKey);
      try {
        const r = await api.post("/document-workflows/preview", {
          title: documentTitle,
          category: libraryCategory,
          workflow,
          page: requestedPage,
          document_id: documentId,
        });
        if (!cancelled) {
          setPreview(r.data);
          setPreviewKey(currentPreviewKey);
        }
      } catch (e) {
        if (!cancelled)
          setPreviewError(
            typeof err(e) === "string"
              ? err(e)
              : "Check the document and field settings, then try again.",
          );
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentPreviewKey, previewAttempt]);
  useEffect(() => {
    const before = (e) => {
      if (dirty && wf) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [dirty, wf]);
  const importSource = (file) =>
    run(async () => {
      if (!blank)
        throw Error("Confirm the upload is a blank reusable template.");
      const form = new FormData();
      form.append("file", file);
      form.append("blank_template", "true");
      const r = await api.post("/document-workflows/sources", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (wf.treatment === "preserve" && r.data.format !== "pdf")
        throw Error("Export this original to PDF to preserve its pages.");
      if (wf.treatment === "rebuild" && r.data.format !== "docx")
        throw Error(
          "Use an editable DOCX for structural rebuilding, or choose Preserve original for this PDF.",
        );
      setSourceName(r.data.filename);
      change({
        source_id: r.data.source_id,
        source_mode: "existing",
        blocks: r.data.blocks,
        fields: r.data.fields,
        completion: r.data.fields.length ? "fill" : wf.completion,
        sop_template: false,
        wording_edited: false,
      });
      setPage(0);
      setSelectedId(r.data.fields[0]?.id || "");
      if (r.data.fields.length) setPanel("prepare");
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ""));
      setPreview(null);
    });
  const save = () =>
    run(async () => {
      const r = doc
        ? await api.put(`/document-workflows/${doc.id}`, payload())
        : await api.post("/document-workflows", payload());
      setDoc(r.data);
      setDirty(false);
      setReview(false);
      return r.data;
    });
  const publish = () =>
    run(async () => {
      if (dirty || !doc || !review || !previewReady)
        throw Error("Save and review the current version first.");
      const data = {
        expected_version: doc.version,
        expected_hash: doc.workflow_hash,
        confirmed: true,
      };
      await api.post(`/document-workflows/${doc.id}/review`, data);
      await api.post(`/document-workflows/${doc.id}/publish`, data);
      setDoc({ ...doc, status: "published" });
      onCreated?.(doc.id);
      onSaved?.();
      return true;
    });
  const advance = async () => {
    if (!ready || busy) return;
    if (panel === "setup") setPanel("prepare");
    else if (panel === "prepare") {
      const saved = dirty || !doc ? await save() : doc;
      if (saved) setPanel("review");
    }
  };
  const addField = (kind) => {
    let next;
    if (kind === "signature") {
      const signer = wf.counter_sign ? "general_manager" : "employee";
      next = addSignPair(wf, signer, page);
    } else
      next = [
        ...wf.fields,
        {
          id: "field_" + Math.random().toString(16).slice(2),
          kind,
          label:
            kind === "checkbox"
              ? "Checkbox"
              : kind === "date"
                ? "Date"
                : "Text field",
          page,
          x: 10,
          y: 20,
          width: kind === "checkbox" ? 4 : 35,
          height: 4,
          required: false,
          signer: "employee",
          existing_name: null,
          options: [],
        },
      ];
    change({ fields: next });
    setSelectedId(next[next.length - 1].id);
  };
  const setCompletion = (value) => {
    let fields = wf.fields,
      counter = wf.counter_sign;
    if (["sign", "fill_sign"].includes(value))
      fields = addSignPair(wf, "employee", page);
    else
      fields = fields.filter(
        (f) =>
          !["signature", "signing_date"].includes(f.kind) ||
          f.signer === "general_manager",
      );
    if (value === "read_only") {
      fields = fields.filter(
        (f) => !["signature", "signing_date"].includes(f.kind),
      );
      counter = false;
    }
    change({ completion: value, fields, counter_sign: counter });
  };
  const counterSign = (checked) => {
    change({
      counter_sign: checked,
      fields: checked
        ? addSignPair(wf, "general_manager", page)
        : wf.fields.filter((f) => f.signer !== "general_manager"),
    });
  };
  const patchField = (id, patch) =>
    change({
      fields: wf.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });
  const setRole = (role, checked) => {
    let next = {
      ...wf,
      onboarding_roles: checked
        ? [...wf.onboarding_roles, role]
        : wf.onboarding_roles.filter((x) => x !== role),
    };
    if (checked) {
      next.counter_sign = true;
      if (next.completion === "read_only") next.completion = "acknowledge";
      next.fields = addSignPair(next, "general_manager", page);
    }
    change(next);
  };
  const currentBlock = wf?.blocks[blockIndex];
  const updateBlock = (patch) =>
    change({
      blocks: wf.blocks.map((b, i) =>
        i === blockIndex ? { ...b, ...patch } : b,
      ),
    });
  return (
    <div className="document-workflow wf-builder">
      {error && (
        <p role="alert" className="wf-error">
          {error}
        </p>
      )}
      <div className="wf-builder-bar">
        <div className="wf-editor-tools">
          <span>
            {doc
              ? `Version ${doc.version} · ${doc.status}`
              : wf.treatment === "preserve"
                ? "Preserve original"
                : "Rebuild with current typography"}
          </span>
          <button
            type="button"
            disabled={busy || !ready || !dirty}
            onClick={save}
          >
            Save draft
          </button>
          <span className="wf-save-state" role="status">
            {busy ? "Working…" : dirty ? "Unsaved changes" : "Draft saved"}
          </span>
        </div>
        <nav className="wf-steps" aria-label="Document creation steps">
          {steps.map(([id, label], index) => (
            <button
              key={id}
              type="button"
              aria-current={panel === id ? "step" : undefined}
              disabled={
                busy || index > stepIndex + 1 || (index > stepIndex && !ready)
              }
              onClick={() => (index > stepIndex ? advance() : setPanel(id))}
            >
              <span aria-hidden="true">{index + 1}</span>
              {label}
            </button>
          ))}
        </nav>
      </div>
      <div
        ref={workspace}
        className="wf-workspace"
        style={{ "--wf-workspace-height": `${workspaceHeight}px` }}
      >
        <div
          className="wf-preview-column"
          aria-busy={ready && !previewReady && !previewError}
        >
          {ready && (!previewReady || previewError) && (
            <div
              className="wf-preview-state"
              role={previewError ? "alert" : "status"}
            >
              {previewError || "Updating document…"}
              {previewError && (
                <button
                  type="button"
                  onClick={() => setPreviewAttempt((n) => n + 1)}
                >
                  Try preview again
                </button>
              )}
            </div>
          )}
          <WorkflowPage
            preview={samePage ? preview : null}
            page={page}
            setPage={setPage}
            fields={wf.fields}
            fitContainer
            canFill={false}
            editing={panel === "prepare"}
            emptyMessage={
              ready
                ? previewError
                  ? "The preview could not load. Check the message above."
                  : "Preparing your document…"
                : "Upload your blank document to begin."
            }
            selected={selected}
            onSelect={setSelectedId}
            onChange={patchField}
          />
        </div>
        <aside
          key={panel}
          className="wf-settings"
          data-step={panel}
          aria-label={steps[stepIndex][1]}
        >
          <h3 ref={stepHeading} tabIndex={-1} className="wf-step-heading">
            {panel === "setup"
              ? "Document"
              : panel === "prepare"
                ? "Fields & signatures"
                : "Ready to publish?"}
          </h3>
          {panel === "setup" && (
            <>
              {!doc && !wf.source_id && (
                <details className="wf-other-options">
                  <summary>Other document options</summary>
                  <button
                    type="button"
                    onClick={() => {
                      setWf(
                        initial(
                          wf.treatment === "preserve" ? "rebuild" : "preserve",
                        ),
                      );
                      setPreview(null);
                      setBlank(false);
                      setSelectedId("");
                    }}
                  >
                    {wf.treatment === "preserve"
                      ? "Create from Word document"
                      : "Use a PDF original"}
                  </button>
                </details>
              )}
              {wf.treatment === "rebuild" && (
                <label>
                  Source
                  <select
                    value={wf.source_mode}
                    onChange={(e) => {
                      if (e.target.value === "new") {
                        if (
                          wf.source_id &&
                          !window.confirm(
                            "Start a new document? The retained original stays private.",
                          )
                        )
                          return;
                        change({
                          source_mode: "new",
                          source_id: null,
                          blocks: [block()],
                          fields: [],
                          sop_template: false,
                        });
                        setPreview(null);
                      } else change({ source_mode: "existing" });
                    }}
                  >
                    <option value="existing">Existing DOCX</option>
                    <option value="new">Start blank</option>
                  </select>
                </label>
              )}
              {wf.source_mode === "existing" && (
                <>
                  <button
                    type="button"
                    className="wf-primary wf-upload-source"
                    disabled={busy || !blank}
                    onClick={() => input.current.click()}
                  >
                    <Upload size={15} />
                    {wf.source_id
                      ? "Replace document"
                      : wf.treatment === "preserve"
                        ? "Upload PDF"
                        : "Upload DOCX"}
                  </button>
                  <label className="wf-check">
                    <input
                      type="checkbox"
                      checked={blank}
                      onChange={(e) => setBlank(e.target.checked)}
                    />
                    This is a blank reusable template, not a completed personal
                    record.
                  </label>
                  <input
                    ref={input}
                    className="sr-only"
                    type="file"
                    accept={wf.treatment === "preserve" ? ".pdf" : ".docx"}
                    onChange={(e) => {
                      if (e.target.files[0]) importSource(e.target.files[0]);
                      e.target.value = "";
                    }}
                  />

                  <p className="wf-note">
                    {sourceName ||
                      "Originals are encrypted and retained privately."}{" "}
                    {wf.treatment === "preserve"
                      ? "PDF pages and existing fields remain intact."
                      : "Supported DOCX headings, lists, tables and page breaks are retained. Complex layouts need a PDF original."}
                  </p>
                </>
              )}

              <label>
                Document title
                <input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setDirty(true);
                    setReview(false);
                  }}
                  maxLength={240}
                />
              </label>
              <label>
                Document type
                <select
                  value={wf.document_type}
                  onChange={(e) => change({ document_type: e.target.value })}
                >
                  {WORKFLOW_TYPES.map(([id, label]) => (
                    <option
                      key={id}
                      value={id}
                      disabled={
                        wf.treatment === "rebuild" && id === "official_form"
                      }
                    >
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Library category
                <input
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setDirty(true);
                    setReview(false);
                  }}
                  placeholder="Optional"
                  maxLength={100}
                />
              </label>
              {wf.treatment === "rebuild" &&
                wf.source_mode === "new" &&
                wf.document_type === "sop_policy" && (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        wf.blocks.some((b) => b.text) &&
                        !window.confirm(
                          "Replace the new draft content with the SOP template?",
                        )
                      )
                        return;
                      change({
                        sop_template: true,
                        blocks: [
                          block("heading", "Purpose"),
                          block(),
                          block("heading", "Procedure"),
                          { ...block("list"), ordered: true },
                          block("heading", "Related information"),
                          block(),
                        ],
                      });
                      document
                        .querySelector(".wf-content-edit")
                        ?.setAttribute("open", "");
                    }}
                  >
                    Use optional SOP template
                  </button>
                )}
            </>
          )}
          {panel === "setup" && wf.treatment === "rebuild" && (
            <details className="wf-content-edit">
              <summary>Edit document content</summary>
              <div className="wf-section-body">
                <label className="wf-check">
                  <input
                    type="checkbox"
                    checked={wf.wording_edited || wf.source_mode === "new"}
                    disabled={wf.source_mode === "new"}
                    onChange={(e) =>
                      change({ wording_edited: e.target.checked })
                    }
                  />
                  Allow wording edits
                </label>
                <label>
                  Document block
                  <select
                    value={blockIndex}
                    onChange={(e) => setBlockIndex(Number(e.target.value))}
                  >
                    {wf.blocks.map((b, i) => (
                      <option key={i} value={i}>
                        {i + 1}. {b.kind} {b.text.slice(0, 20)}
                      </option>
                    ))}
                  </select>
                </label>
                {currentBlock && (
                  <>
                    <p className="wf-note">
                      {currentBlock.kind} · order {blockIndex + 1} of{" "}
                      {wf.blocks.length}
                    </p>
                    {currentBlock.kind === "table" ? (
                      <div className="wf-table-edit">
                        {currentBlock.rows.map((row, r) => (
                          <label key={r}>
                            Row {r + 1}
                            {row.map((cell, c) => (
                              <input
                                key={c}
                                aria-label={`Row ${r + 1} column ${c + 1}`}
                                value={cell}
                                disabled={
                                  wf.source_mode === "existing" &&
                                  !wf.wording_edited
                                }
                                onChange={(e) =>
                                  updateBlock({
                                    rows: currentBlock.rows.map((x, i) =>
                                      i === r
                                        ? x.map((v, j) =>
                                            j === c ? e.target.value : v,
                                          )
                                        : x,
                                    ),
                                  })
                                }
                              />
                            ))}
                          </label>
                        ))}
                      </div>
                    ) : (
                      currentBlock.kind !== "page_break" && (
                        <textarea
                          rows={5}
                          aria-label="Block wording"
                          value={currentBlock.text}
                          disabled={
                            wf.source_mode === "existing" && !wf.wording_edited
                          }
                          onChange={(e) =>
                            updateBlock({ text: e.target.value })
                          }
                        />
                      )
                    )}
                  </>
                )}
                {wf.source_mode === "new" && (
                  <label>
                    Add block
                    <select
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const b = block(e.target.value);
                        if (b.kind === "table")
                          b.rows = [
                            ["", ""],
                            ["", ""],
                          ];
                        change({ blocks: [...wf.blocks, b] });
                        setBlockIndex(wf.blocks.length);
                      }}
                    >
                      <option value="">Choose structure</option>
                      {[
                        "paragraph",
                        "heading",
                        "list",
                        "table",
                        "page_break",
                      ].map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </select>
                  </label>
                )}
                <p className="wf-note">
                  Existing order and structure stay fixed. Unsupported artwork,
                  merged tables, contents fields and complex layouts are
                  rejected rather than flattened.
                </p>
              </div>
            </details>
          )}
          {panel === "prepare" && (
            <section className="wf-prepare-section">
              <h4>Choose a field on the document</h4>
              <details className="wf-manual-fields">
                <summary>Add a missing field</summary>
                <div className="wf-field-tools">
                  <button
                    type="button"
                    disabled={!["fill", "fill_sign"].includes(wf.completion)}
                    onClick={() => addField("text")}
                  >
                    <Type size={15} />
                    Text field
                  </button>
                  <button
                    type="button"
                    disabled={!["fill", "fill_sign"].includes(wf.completion)}
                    onClick={() => addField("checkbox")}
                  >
                    <SquareCheck size={15} />
                    Checkbox
                  </button>
                  <button
                    type="button"
                    disabled={!["fill", "fill_sign"].includes(wf.completion)}
                    onClick={() => addField("date")}
                  >
                    <CalendarDays size={15} />
                    Date picker
                  </button>
                </div>
              </details>
              {selected ? (
                <>
                  {!selected.read_only && selected.kind !== "choice" && (
                    <label>
                      Input type
                      <select
                        aria-label="Input type"
                        value={selected.kind}
                        onChange={(e) => {
                          const kind = e.target.value;
                          let fields = wf.fields.map((f) =>
                            f.id === selected.id
                              ? {
                                  ...f,
                                  kind,
                                  prefill: "none",
                                  prefill_text: "",
                                  signer: selected.signer || "employee",
                                }
                              : f,
                          );
                          if (!["signature", "signing_date"].includes(kind))
                            fields = fields.filter(
                              (f) => f.id !== selected.id + "_date",
                            );
                          if (
                            selected.signer !== "general_manager" &&
                            kind === "signature" &&
                            !fields.some(
                              (f) =>
                                f.kind === "signing_date" &&
                                f.signer === "employee",
                            )
                          )
                            fields = [
                              ...fields,
                              {
                                ...signPair("employee", selected.page)[1],
                                id: selected.id + "_date",
                              },
                            ];
                          if (
                            selected.signer !== "general_manager" &&
                            kind === "signing_date" &&
                            !fields.some(
                              (f) =>
                                f.kind === "signature" &&
                                f.signer === "employee",
                            )
                          )
                            fields = [
                              ...fields,
                              signPair("employee", selected.page)[0],
                            ];
                          change({
                            fields,
                            completion: fields.some(
                              (f) =>
                                f.kind === "signature" &&
                                f.signer === "employee",
                            )
                              ? "fill_sign"
                              : ["sign", "fill_sign"].includes(wf.completion)
                                ? "fill"
                                : wf.completion,
                          });
                        }}
                      >
                        <option
                          value="text"
                          disabled={selected.signer === "general_manager"}
                        >
                          Text
                        </option>
                        <option
                          value="date"
                          disabled={selected.signer === "general_manager"}
                        >
                          Date picker
                        </option>
                        <option
                          value="checkbox"
                          disabled={selected.signer === "general_manager"}
                        >
                          Checkbox
                        </option>
                        <option value="signature">
                          {selected.signer === "general_manager"
                            ? "General Manager signature"
                            : "Employee signature"}
                        </option>
                        <option value="signing_date">
                          Signing date (automatic)
                        </option>
                      </select>
                    </label>
                  )}
                  <label>
                    Field label
                    <input
                      value={selected.label}
                      disabled={
                        selected.read_only ||
                        (selected.existing_name && selected.kind === "choice")
                      }
                      onChange={(e) =>
                        patchField(selected.id, { label: e.target.value })
                      }
                    />
                  </label>
                  {selected.kind === "text" && !selected.read_only && (
                    <>
                      <label>
                        Auto-fill
                        <select
                          value={selected.prefill || "none"}
                          onChange={(e) => {
                            const mode = e.target.value;
                            patchField(selected.id, {
                              prefill: mode,
                              ...([
                                "Text field",
                                "Company name",
                                "Date completed",
                              ].includes(selected.label)
                                ? {
                                    label:
                                      mode === "company"
                                        ? "Company name"
                                        : mode === "completion_date"
                                          ? "Date completed"
                                          : "Text field",
                                  }
                                : {}),
                              prefill_text:
                                mode === "company"
                                  ? "Green Massage of Plantation"
                                  : mode === "text"
                                    ? selected.prefill_text || ""
                                    : "",
                            });
                          }}
                        >
                          <option value="none">Person enters the text</option>
                          <option value="company">Company name</option>
                          <option value="text">Saved text</option>
                          <option value="completion_date">
                            Date completed (automatic)
                          </option>
                        </select>
                      </label>
                      {["company", "text"].includes(selected.prefill) && (
                        <label>
                          {selected.prefill === "company"
                            ? "Company name"
                            : "Saved text"}
                          <input
                            value={selected.prefill_text || ""}
                            maxLength={2000}
                            onChange={(e) =>
                              patchField(selected.id, {
                                prefill_text: e.target.value,
                              })
                            }
                          />
                        </label>
                      )}
                      {selected.prefill && selected.prefill !== "none" && (
                        <p className="wf-note">
                          {selected.prefill === "completion_date"
                            ? "Added on completion using the boutique’s local date. Separate from signing dates."
                            : "Saved with this template. Recipients cannot change it."}
                        </p>
                      )}
                    </>
                  )}
                  <label className="wf-check">
                    <input
                      type="checkbox"
                      checked={selected.required}
                      disabled={
                        selected.read_only ||
                        (selected.existing_name && selected.kind === "choice")
                      }
                      onChange={(e) =>
                        patchField(selected.id, { required: e.target.checked })
                      }
                    />
                    Required
                  </label>
                  {selected.existing_name ? (
                    <p className="wf-note">
                      Detected from the PDF. Its position stays fixed; choose
                      how the employee completes it above.
                    </p>
                  ) : (
                    <>
                      <p className="wf-note">
                        Drag to move; use the corner handle to resize. Arrow
                        keys move; Alt + arrow keys resize.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          change({
                            fields: wf.fields.filter(
                              (f) =>
                                f.id !== selected.id &&
                                f.id !== selected.id + "_date" &&
                                !(
                                  selected.kind === "signing_date" &&
                                  f.id === selected.id.replace(/_date$/, "")
                                ),
                            ),
                          });
                          setSelectedId("");
                        }}
                      >
                        Remove field
                      </button>
                    </>
                  )}
                </>
              ) : (
                <p className="wf-note">
                  Select a box on the document to choose its input. Use the
                  buttons above only for missing fields.
                </p>
              )}
            </section>
          )}
          {panel === "prepare" && (
            <>
              <label>
                What should the employee do?
                <select
                  aria-label="Employee action"
                  value={wf.completion}
                  onChange={(e) => setCompletion(e.target.value)}
                >
                  {COMPLETIONS.map(([id, label]) => (
                    <option value={id} key={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wf-check">
                <input
                  type="checkbox"
                  checked={wf.counter_sign}
                  disabled={
                    wf.completion === "read_only" ||
                    !!wf.onboarding_roles.length
                  }
                  onChange={(e) => counterSign(e.target.checked)}
                />
                General Manager countersigns
              </label>
              <p className="wf-note">
                <Lock size={13} /> Each person signs from their own account.
                Signing dates are added automatically.
              </p>
              <p className="wf-note">
                Position each signature and date on the document before review.
              </p>
              {wf.fields
                .filter((f) => f.kind === "signature")
                .map((f) => (
                  <button
                    type="button"
                    key={f.id}
                    onClick={() => {
                      setSelectedId(f.id);
                      setPage(f.page);
                      document
                        .querySelector(".wf-prepare-section input")
                        ?.focus();
                    }}
                  >
                    <PenLine size={15} />
                    {f.label}
                  </button>
                ))}
            </>
          )}
          {panel === "prepare" && (
            <details className="wf-onboarding">
              <summary>Include in onboarding (optional)</summary>
              <div className="wf-section-body">
                <p className="wf-note">
                  Select the roles that need this document during onboarding.
                  The employee must complete it and their boutique GM must
                  countersign. Existing employees are not automatically
                  enrolled.
                </p>
                {ROLES.map(([id, label]) => (
                  <label key={id} className="wf-check">
                    <input
                      type="checkbox"
                      checked={wf.onboarding_roles.includes(id)}
                      onChange={(e) => setRole(id, e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
                <p className="wf-note">
                  Publish the template, then assign the configured role packet
                  from the employee’s file. Each assignment keeps its exact
                  document version.
                </p>
              </div>
            </details>
          )}
          {panel === "review" && (
            <>
              <dl className="wf-review-summary">
                <div>
                  <dt>Document</dt>
                  <dd>{title}</dd>
                </div>
                <div>
                  <dt>Employee action</dt>
                  <dd>
                    {COMPLETIONS.find(([id]) => id === wf.completion)?.[1]}
                  </dd>
                </div>
                <div>
                  <dt>Fields</dt>
                  <dd>
                    {wf.fields.length} on {preview?.pages?.length || "—"}{" "}
                    {preview?.pages?.length === 1 ? "page" : "pages"}
                  </dd>
                </div>
                <div>
                  <dt>Onboarding</dt>
                  <dd>
                    {wf.onboarding_roles.length
                      ? ROLES.filter(([id]) => wf.onboarding_roles.includes(id))
                          .map(([, name]) => name)
                          .join(", ")
                      : "Not included"}
                  </dd>
                </div>
                <div>
                  <dt>GM countersignature</dt>
                  <dd>{wf.counter_sign ? "Required" : "Not required"}</dd>
                </div>
              </dl>
              <p className="wf-note">
                Blank templates are visible to everyone signed in. Completed
                records are private to the employee and authorized onboarding
                managers.
              </p>
              {dirty && (
                <p className="wf-note">
                  Return to Fields & signatures and continue to save your latest
                  changes.
                </p>
              )}
              {doc && (
                <details className="wf-review-files">
                  <summary>Download & saved files</summary>
                  <div className="wf-section-body">
                    <button
                      type="button"
                      disabled={busy || dirty}
                      onClick={() =>
                        run(() =>
                          workflowDownload(
                            `/document-workflows/${doc.id}/export`,
                            "template.pdf",
                          ),
                        )
                      }
                    >
                      <Download size={15} />
                      Download PDF
                    </button>
                    {wf.source_id && doc?.created_by === user?.user_id && (
                      <button
                        type="button"
                        onClick={() =>
                          run(() =>
                            workflowDownload(
                              `/document-workflows/${doc.id}/original`,
                              "retained-original",
                            ),
                          )
                        }
                      >
                        Download retained original
                      </button>
                    )}
                    <button
                      type="button"
                      className="wf-link"
                      onClick={() => {
                        if (
                          !dirty ||
                          window.confirm("Discard unsaved edits?")
                        ) {
                          onCreated?.(doc.id);
                          onSaved?.();
                        }
                      }}
                    >
                      Open saved document
                    </button>
                  </div>
                </details>
              )}
            </>
          )}
          <div className="wf-step-actions">
            {panel === "review" && (
              <>
                {" "}
                <label className="wf-check">
                  <input
                    type="checkbox"
                    checked={review}
                    disabled={busy || dirty || !doc || !previewReady}
                    onChange={(e) => setReview(e.target.checked)}
                  />
                  I reviewed every page of this saved version.
                </label>
              </>
            )}
            {stepIndex > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setPanel(steps[stepIndex - 1][0])}
              >
                Back
              </button>
            )}
            {panel === "review" ? (
              <button
                type="button"
                className="wf-primary"
                disabled={busy || dirty || !doc || !review || !previewReady}
                onClick={publish}
              >
                {busy ? "Publishing…" : "Publish document"}
              </button>
            ) : (
              <button
                type="button"
                className="wf-primary"
                disabled={busy || !ready || !previewReady}
                onClick={advance}
              >
                {busy
                  ? "Saving…"
                  : panel === "setup"
                    ? "Continue to fields"
                    : "Save & review"}
                <ArrowRight size={16} />
              </button>
            )}
            {!ready && (
              <p className="wf-note">
                {!wf.source_id && wf.source_mode === "existing"
                  ? "Upload a blank document to continue."
                  : "Enter a document title to continue."}
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

// PDF templates use the approved single workspace; Word rebuilding retains its structural editor.
export default function DocumentWorkflowBuilder(props) {
  const [word, setWord] = useState(
    props.existing?.workflow?.treatment === "rebuild",
  );
  return word ? (
    <LegacyDocumentWorkflowBuilder {...props} initialTreatment="rebuild" />
  ) : (
    <DocumentCanvasBuilder {...props} onWord={() => setWord(true)} />
  );
}
